import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { resolveUrl } from "@/lib/api";
import { DEFAULT_HOME, mergeHome } from "@/lib/homeDefaults";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import JourneyCard from "@/components/JourneyCard";
import Row from "@/components/Row";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";

export default function Home() {
    const { user } = useAuth();
    const [featured, setFeatured] = useState([]);
    const [all, setAll] = useState([]);
    const [continueList, setContinueList] = useState([]);
    const [categories, setCategories] = useState([]);
    const [home, setHome] = useState(DEFAULT_HOME);

    useEffect(() => {
        api.get("/journeys", { params: { featured: true } }).then(r => setFeatured(r.data));
        api.get("/journeys").then(r => setAll(r.data));
        api.get("/categories").then(r => setCategories(r.data));
        api.get("/me/continue").then(r => setContinueList(r.data)).catch(() => {});
        api.get("/site/home").then(r => setHome(mergeHome(r.data?.content))).catch(() => {});
    }, []);

    const hero = featured[0];
    const featuredCfg = home.featured;

    return (
        <div className="min-h-screen relative grain">
            <Header />

            {/* Personal welcome hero */}
            {hero && (
                <section data-testid="home-hero" className="relative overflow-hidden">
                    <img src={resolveUrl(hero.cover)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#F8F8F5] via-[#F8F8F5]/40 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#F8F8F5]/85 via-[#F8F8F5]/30 to-transparent" />
                    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
                        <div className="max-w-2xl">
                            <div className="overline text-[#5C5C5C] rise rise-1">Olá, {user?.name?.split(" ")[0]} — em destaque agora</div>
                            <h1 className="font-display font-light text-4xl md:text-6xl mt-4 leading-tight rise rise-2">
                                {hero.title}
                            </h1>
                            <p className="font-body text-[#1A1A1A]/80 mt-5 max-w-xl text-lg rise rise-3">
                                {hero.subtitle}
                            </p>
                            <div className="flex flex-wrap gap-3 mt-8 rise rise-4">
                                <Link to={`/jornada/${hero.slug}`} data-testid="home-hero-watch" className="btn-primary">
                                    <Play className="w-4 h-4 fill-white" /> Continuar
                                </Link>
                                <Link to="/jornadas" data-testid="home-hero-explore" className="btn-holo">Explorar tudo</Link>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {continueList.length > 0 && (
                <Row title="Continue assistindo" eyebrow="Sua jornada" testId="row-continue">
                    {continueList.map((c) => (
                        <Link key={c.journey.id + c.module.id} to={`/assistir/${c.journey.slug}/${c.module.id}`}
                              className="journey-card" data-testid={`continue-${c.journey.slug}`}>
                            <div className="relative">
                                <img src={resolveUrl(c.journey.cover)} alt="" className="cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                    <div className="overline text-white/80">Módulo {c.module.order}</div>
                                    <div className="font-display text-white text-lg leading-snug line-clamp-2">{c.module.title}</div>
                                </div>
                            </div>
                            <div className="p-5">
                                <div className="font-body text-sm text-[#5C5C5C] line-clamp-1">{c.journey.title}</div>
                                <div className="bar mt-3"><div style={{ width: `${Math.min(100, (c.position_seconds / (c.module.duration_minutes * 60)) * 100 || 5)}%` }} /></div>
                            </div>
                        </Link>
                    ))}
                </Row>
            )}

            <Row
                title={featuredCfg.title || "Jornadas em destaque"}
                eyebrow={featuredCfg.eyebrow || "Curadoria do Instituto"}
                testId="row-home-featured"
                columns={featuredCfg.columns || 4}
            >
                {featured.map(j => <JourneyCard key={j.id} journey={j} />)}
            </Row>

            <Row title="Todas as jornadas" eyebrow="Catálogo" testId="row-all">
                {all.map(j => <JourneyCard key={j.id} journey={j} />)}
            </Row>

            <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20">
                <div className="overline text-[#5C5C5C]">Navegue por</div>
                <h2 className="font-display text-3xl md:text-4xl mt-1 tracking-tight">Categorias</h2>
                <div className="grid md:grid-cols-4 gap-5 mt-8">
                    {categories.map(c => (
                        <Link key={c.id} to={`/jornadas?cat=${c.slug}`}
                              data-testid={`category-${c.slug}`}
                              className="p-7 rounded-2xl bg-white border border-black/5 hover:border-[rgba(0,212,212,0.4)] transition-all hover:-translate-y-1">
                            <div className="overline text-[#5C5C5C]">Categoria</div>
                            <div className="font-display text-xl mt-2 tracking-tight">{c.name}</div>
                            <div className="font-body text-sm text-[#5C5C5C] mt-2 line-clamp-2">{c.description}</div>
                        </Link>
                    ))}
                </div>
            </section>

            <Footer />
        </div>
    );
}
